import { useGetRenewVisaDocumentGroupInfo } from "@/api/services/main/renew-visa";
import { Button } from "@/components/elements/Button";
import { Modal } from "@/components/elements/Modal";
import { RefreshEmployeeInfoBtn } from "@/features/renew-visa/components/applicationStep/formApplication/employeeApplication/RefreshEmployeeInfoBtn";
import { useGetPreventEmployeeApplicationModalState } from "@/features/renew-visa/hook/useGetPreventEmployeeApplicationModalState";
import { TEmployeeApplicationForm } from "@/features/renew-visa/types/employeeApplicationForm";
import { AppRoutes } from "@/routes/config";
import { RENEW_VISA_DOCUMENT_STATUS } from "@/types/enum";
import { toNumber } from "@/utils/number";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";

type TPreventEmployeeApplicationModalProps = {
  setIsEditable?: (v: boolean) => void;
};

export const PreventEmployeeApplicationModal: FC<TPreventEmployeeApplicationModalProps> = ({ setIsEditable }) => {
  const methods = useFormContext<TEmployeeApplicationForm>();

  const { data: renewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const { control } = methods;

  const employeeIdWatchField = useWatch({ control, name: "employeeId" });
  const contractStartDateWatchField = useWatch({ control, name: "contractStartDate" });
  const contractEndDateWatchField = useWatch({ control, name: "contractEndDate" });
  const relatedActivityJapaneseCertificationTwoWatchField = useWatch({ control, name: "relatedActivityJapaneseCertificationTwo" });
  const relatedActivitySkillCertificationOneWatchField = useWatch({ control, name: "relatedActivitySkillCertificationOne" });
  const employeeInfoPassportPermanentAddressWatchField = useWatch({ control, name: "employeeInfoPassportPermanentAddress" });
  const contractMonthlySalaryWatchField = useWatch({ control, name: "contractMonthlySalary" });
  const { alwaysOpenPreventEmployeeApplicationModal } = useGetPreventEmployeeApplicationModalState();

  const employeeEditRouteWithHash = useMemo(() => {
    if (!employeeInfoPassportPermanentAddressWatchField)
      return `${getDynamicRoute(AppRoutes.employeeEdit, { id: employeeIdWatchField })}#passport_permanent_address_input`;
    else if (!contractStartDateWatchField)
      return `${getDynamicRoute(AppRoutes.employeeEdit, { id: employeeIdWatchField })}#contract_start_date_input`;
    else if (!contractEndDateWatchField) return `${getDynamicRoute(AppRoutes.employeeEdit, { id: employeeIdWatchField })}#contract_end_date_input`;
    else if (!toNumber(contractMonthlySalaryWatchField))
      return `${getDynamicRoute(AppRoutes.employeeEdit, { id: employeeIdWatchField })}#contract_monthly_salary_input`;
    else if (!relatedActivitySkillCertificationOneWatchField)
      return `${getDynamicRoute(AppRoutes.employeeEdit, { id: employeeIdWatchField })}#career_skill_certification_one_input`;
    else if (!relatedActivityJapaneseCertificationTwoWatchField)
      return `${getDynamicRoute(AppRoutes.employeeEdit, { id: employeeIdWatchField })}#career_japanese_certification_one_input`;
  }, [
    employeeIdWatchField,
    contractStartDateWatchField,
    contractEndDateWatchField,
    relatedActivityJapaneseCertificationTwoWatchField,
    relatedActivitySkillCertificationOneWatchField,
    employeeInfoPassportPermanentAddressWatchField,
    contractMonthlySalaryWatchField,
  ]);

  return (
    <Modal
      isOpen={
        alwaysOpenPreventEmployeeApplicationModal && !!renewVisaDocumentGroup && renewVisaDocumentGroup.status !== RENEW_VISA_DOCUMENT_STATUS.COMPLETE
      }
      enableFooter={false}
      title="在留期間更新には、次の特定技能外国人に関する情報をご入力ください。"
      enableCloseIcon={false}
    >
      <Box component="ul" paddingTop={2} paddingBottom={4}>
        {!employeeInfoPassportPermanentAddressWatchField && (
          <Typography component="li" variant="body14">
            本国における居住地
          </Typography>
        )}
        {!contractStartDateWatchField && (
          <Typography component="li" variant="body14">
            雇用契約期間（始期）
          </Typography>
        )}
        {!contractEndDateWatchField && (
          <Typography component="li" variant="body14">
            雇用契約期間（終期）
          </Typography>
        )}
        {!toNumber(contractMonthlySalaryWatchField) && (
          <Typography component="li" variant="body14">
            月額報酬（円）【0より大きい値を入力してください】
          </Typography>
        )}
        {!relatedActivitySkillCertificationOneWatchField && (
          <Typography component="li" variant="body14">
            技能水準　証明1
          </Typography>
        )}
        {!relatedActivityJapaneseCertificationTwoWatchField && (
          <Typography component="li" variant="body14">
            日本語能力　証明１
          </Typography>
        )}
      </Box>
      <Stack flexDirection="row" justifyContent="center" alignItems="center" gap={2}>
        <RefreshEmployeeInfoBtn setIsEditable={setIsEditable} />
        <Button variant="primary" target="_blank" size="sm" component={Link} to={employeeEditRouteWithHash}>
          特定技能外国人の編集へ
        </Button>
      </Stack>
    </Modal>
  );
};
